// One engine pass shared by the map and the results panel. Mirrors the
// branching in the Streamlit app: summarize Box A, then a 2- or 3-leg chain.

import {
  conditionalChain,
  summarizeBox,
  type BoxSummary,
  type ChainResult,
} from "./probability";
import type { Box, Filters, Storm, TransitInfo } from "./types";

export type Leg = "a" | "ab" | "abc";

export interface EngineView {
  summaryA: BoxSummary | null;
  chain: ChainResult | null;
  /** storm_id -> deepest leg reached, for map colouring. */
  legByStormId: Map<string, Leg>;
  /** Storms to list in the results panel (deepest available leg). */
  displayStorms: TransitInfo[];
  displayLabel: string;
}

export function computeView(
  tracks: Storm[],
  boxA: Box | null,
  boxB: Box | null,
  boxC: Box | null,
  filters: Filters,
): EngineView {
  const legByStormId = new Map<string, Leg>();
  let summaryA: BoxSummary | null = null;
  let chain: ChainResult | null = null;
  let displayStorms: TransitInfo[] = [];
  let displayLabel = "";

  if (!boxA) {
    return { summaryA, chain, legByStormId, displayStorms, displayLabel };
  }

  summaryA = summarizeBox(tracks, boxA, filters);
  for (const s of summaryA.storms) legByStormId.set(s.storm_id, "a");
  displayStorms = summaryA.storms;
  displayLabel = "Box A";

  if (boxB) {
    chain = conditionalChain(tracks, boxA, boxB, boxC ?? null, filters);
    for (const s of chain.storms_ab) legByStormId.set(s.storm_id, "ab");
    if (chain.storms_abc) {
      for (const s of chain.storms_abc) legByStormId.set(s.storm_id, "abc");
      displayStorms = chain.storms_abc;
      displayLabel = "A → B → C";
    } else {
      displayStorms = chain.storms_ab;
      displayLabel = "A → B";
    }
  }

  return { summaryA, chain, legByStormId, displayStorms, displayLabel };
}
